/**
 * フォロー状況の 4 値化（課題 #71 / BE PR #143）。
 *
 * 状況は 未対応 / 対応中 / 経過観察 / 完了 の 4 値で、今の状況から選べる値は遷移の表で決まる。
 * 表示名と遷移の表は、BE の options API（GET /v1/healthcare/interview-records/options）から受け取る。
 *
 * BE の反映と FE の反映の順番を問わないよう、options API が無い（または読めない）ときは、
 * BE PR #143 と同じ表を FE の側で使う。4 値になる前の BE は自由入力を受け付けるので、
 * FE が 4 値のコードを送っても通る。表を変えるときは BE と合わせる（正本は BE）。
 */
import { RequestError, type InterviewRecordOptionsResponse } from '../api/client';

export const FOLLOW_STATUS_CODES = ['not_started', 'in_progress', 'observing', 'done'] as const;
export type FollowStatusCode = (typeof FOLLOW_STATUS_CODES)[number];

export interface FollowStatusOption {
  code: string;
  label: string;
}

export interface FollowSpec {
  statuses: FollowStatusOption[];
  transitions: Record<string, string[]>;
  /** options API から受け取った（false なら FE の表を使っている） */
  fromApi: boolean;
}

/** BE PR #143（app/services/interview_records.py）と同じ表 */
export const FALLBACK_FOLLOW_SPEC: FollowSpec = {
  statuses: [
    { code: 'not_started', label: '未対応' },
    { code: 'in_progress', label: '対応中' },
    { code: 'observing', label: '経過観察' },
    { code: 'done', label: '完了' },
  ],
  transitions: {
    not_started: ['in_progress'],
    in_progress: ['observing', 'done'],
    observing: ['in_progress', 'done'],
    done: ['in_progress'],
  },
  fromApi: false,
};

/** options API の応答から、フォロー状況の表を取り出す。形が合わなければ FE の表を使う */
export function followSpecFrom(options: InterviewRecordOptionsResponse | null | undefined): FollowSpec {
  const statuses = (options?.follow_statuses ?? [])
    .filter((o) => o && typeof o.code === 'string' && !o.disabled)
    .map((o) => ({ code: o.code, label: o.label || o.code }));
  const transitions = options?.follow_transitions;
  if (statuses.length === 0 || !transitions || typeof transitions !== 'object') return FALLBACK_FOLLOW_SPEC;
  return { statuses, transitions, fromApi: true };
}

/** 4 値のどれかか（それ以外は 4 値になる前の自由入力の値） */
export function isKnownFollowStatus(spec: FollowSpec, value: string | null | undefined): boolean {
  return value != null && spec.statuses.some((s) => s.code === value);
}

/**
 * 今の状況から選べる値。今の値（備考だけ直すため）と、遷移の表の行き先。
 * まだ状況が無い方と、自由入力の値の方は「未対応」から始まるとみなす（BE と同じ扱い）。
 */
export function allowedFollowStatuses(spec: FollowSpec, current: string | null | undefined): string[] {
  const start = isKnownFollowStatus(spec, current) ? (current as string) : 'not_started';
  const next = spec.transitions[start] ?? [];
  const known = new Set(spec.statuses.map((s) => s.code));
  return [start, ...next].filter((code, i, all) => known.has(code) && all.indexOf(code) === i);
}

export function followStatusLabel(spec: FollowSpec, code: string | null | undefined): string {
  if (code == null) return '';
  return spec.statuses.find((s) => s.code === code)?.label ?? code;
}

/** 409 FOLLOW_TRANSITION_NOT_ALLOWED の中身（今の状況と、選べる値） */
export interface FollowTransitionError {
  currentStatus: string | null;
  allowedStatuses: string[];
}

export function followTransitionErrorFrom(err: unknown): FollowTransitionError | null {
  if (!(err instanceof RequestError) || err.status !== 409 || err.errorCode !== 'FOLLOW_TRANSITION_NOT_ALLOWED') {
    return null;
  }
  const d = (err.details && typeof err.details === 'object' ? err.details : {}) as Record<string, unknown>;
  return {
    currentStatus: typeof d.current_status === 'string' ? d.current_status : null,
    allowedStatuses: Array.isArray(d.allowed_statuses)
      ? d.allowed_statuses.filter((v): v is string => typeof v === 'string')
      : [],
  };
}
